import React, { useEffect, useRef, useContext } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { CursorContext } from "../context/CursorContext";

gsap.registerPlugin(ScrollTrigger); // 🧩 Enable scroll animations

const HeadingAnimation = ({ text, noSplit = false }) => {
  const component = useRef(null); // 🧩 Ref to the heading element
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);

  useEffect(() => {
    const q = gsap.utils.selector(component); // 🧩 Local GSAP selector

    const animate = () => {
      gsap.fromTo(
        q(".name-animation"), // 🧩 Animate each character span
        {
          x: -100,
          opacity: 0,
          rotate: -10,
        },
        {
          x: 0,
          opacity: 1,
          rotate: 0,
          ease: "elastic.out(1,0.3)",
          duration: 0.9,
          transformOrigin: "left top",
          stagger: {
            each: 0.1,
            from: "random",
          },
        },
      );
    };

    const trigger = ScrollTrigger.create({
      trigger: component.current,
      scroller: document.querySelector(".overflow-y-scroll"), // 🧩 Use custom scroll container
      start: "top bottom",
      end: "bottom top",
      onEnter: animate,
      onEnterBack: animate,
    });

    return () => trigger.kill(); // 🧩 Cleanup
  }, []);

  // 🧩 Turn each char into an animated span
  const renderLetters = (word, colorClass) =>
    word.split("").map((char, i) => (
      <span
        key={i}
        className={`name-animation inline-block opacity-0 ${colorClass}`}
      >
        {char === " " ? "\u00A0" : char}
      </span>
    ));

  // 🧩 Layout logic: single or split mode
  const content = () => {
    if (noSplit) {
      return (
        <span className="text-purple-400">{renderLetters(text, "single")}</span>
      );
    }

    const words = text.trim().split(/\s+/);
    const first = words[0] || "";
    const second = words.slice(1).join(" ") || "";

    return (
      <>
        <span className="block text-purple-400">
          {renderLetters(first, "first")}
        </span>
        {second && (
          <span className="block text-purple-300">
            {renderLetters(second, "last")}
          </span>
        )}
      </>
    );
  };

  return (
    <h1
      className="mb-1 text-[clamp(3rem,18vmin,20rem)] font-extrabold font-secondary leading-none tracking-tighter"
      ref={component}
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      {content()}
    </h1>
  );
};

export default HeadingAnimation;
